import { useCallback, useState } from 'react'

import { showAlert, showConfirmAlert, showError } from '_metronic/helpers'
import { MSG_SEM_PERMISSAO_ESUS_REENVIO, usePermissaoEsusReenvio } from './_esusEnvioPermissao'
import { EsusEnvioStatus } from './_models'
import { reenviarEsusEnvio } from './_requests'

/** Reenviar não faz sentido para o que já está transmitido ou em transmissão. */
export const podeReenviarStatus = (status?: EsusEnvioStatus): boolean =>
  status !== 'ENVIADO' && status !== 'ENVIANDO'

export const MSG_REENVIO_CONFLITO =
  'Este envio já foi transmitido ao PEC (ou está em transmissão) e não pode ser reenviado.'

/**
 * Reenvio manual com confirmação (AC3), compartilhado pela linha da lista e pelo detalhe.
 *
 * A confirmação é obrigatória e vem **antes** de qualquer requisição: cancelar não dispara POST
 * nenhum. A empresa não é parâmetro: o backend resolve o tenant do envio pelo próprio registro,
 * dentro do escopo do usuário autenticado. O `confirmButtonText` customizado não é cosmético — sem ele o `showConfirmAlert` cai no
 * default "Sim, excluir!" com botão `btn-danger`, e reenviar não é ação destrutiva (bug já corrigido
 * em E5a).
 */
export const useReenvioEsus = () => {
  const podeReenviar = usePermissaoEsusReenvio()
  const [reenviando, setReenviando] = useState(false)

  const reenviar = useCallback(
    async (envioId: number, aoConcluir?: () => void): Promise<boolean> => {
      if (!podeReenviar) {
        showAlert({ title: 'Sem permissão', text: MSG_SEM_PERMISSAO_ESUS_REENVIO, icon: 'warning' })
        return false
      }

      const confirmado = await showConfirmAlert({
        title: 'Reenviar ficha ao e-SUS?',
        text: 'A Ficha de Atendimento Individual será transmitida novamente ao PEC.',
        icon: 'question',
        confirmButtonText: 'Sim, reenviar',
      })
      if (!confirmado) return false

      setReenviando(true)
      try {
        await reenviarEsusEnvio(envioId)
        showAlert({
          title: 'Reenvio solicitado',
          text: 'A ficha foi enviada novamente ao PEC.',
          icon: 'success',
        })
        aoConcluir?.()
        return true
      } catch (err: any) {
        const status = err?.response?.status
        if (status === 409) {
          // 409 não é erro de validação: o registro mudou de estado por baixo da tela. Avisa e
          // recarrega para a linha/detalhe refletirem o estado real.
          showAlert({ title: 'Reenvio não permitido', text: MSG_REENVIO_CONFLITO, icon: 'info' })
          aoConcluir?.()
        } else if (status === 403) {
          showAlert({
            title: 'Sem permissão',
            text: MSG_SEM_PERMISSAO_ESUS_REENVIO,
            icon: 'warning',
          })
        } else {
          // `showError` já lê o envelope `GenericResponse` (`data.data.errors` / `data.data.message`)
          // usado pelo `GlobalExceptionHandler`, e cai numa mensagem genérica se o formato não bater.
          showError(err)
        }
        return false
      } finally {
        setReenviando(false)
      }
    },
    [podeReenviar]
  )

  return { podeReenviar, reenviando, reenviar }
}
